/// <reference types="vite/client" />
import { useEffect, useRef, useState } from 'react'
import type { LngLatBounds, Map as MapLibreMap, Marker } from 'maplibre-gl'
import workerUrl from 'maplibre-gl/dist/maplibre-gl-worker.mjs?worker&url'

import { cn } from '@/lib/utils'
import { stopTitle, type RouteStop } from '@/src/lib/parcels'

/**
 * A parcel's way to the warehouse: the places its courier scanned it, as
 * numbered stops in time order, joined by a faint dashed line with arrows -
 * the points are the centres of the courier's scan areas, not roads, so the
 * map does not pretend to know the path. The latest stop stands out; pressing
 * a stop selects it (and its scans in the timeline).
 *
 * The basemap is OpenFreeMap's Positron, or its Dark style in dark mode:
 * light, low-detail vector maps, free for commercial use with no key.
 * MapLibre loads only when a map is shown, so pages without one stay light.
 */

export const MAP_STYLES = {
  light: 'https://tiles.openfreemap.org/styles/positron',
  dark: 'https://tiles.openfreemap.org/styles/dark',
}

const SOURCE = 'route'
const LINE_LAYER = 'route-line'
const ARROW_LAYER = 'route-arrows'
const ARROW_IMAGE = 'route-arrow'

function isDark() {
  return typeof document !== 'undefined' && document.documentElement.classList.contains('dark')
}

function token(name: string, fallback: string) {
  if (typeof document === 'undefined') return fallback
  return getComputedStyle(document.documentElement).getPropertyValue(name).trim() || fallback
}

/** The line's colour: Helm's route blue in light mode, its lighter "moving" blue in dark. */
function lineColor(dark: boolean) {
  return dark ? token('--moving', '#93c5fd') : token('--route-actual', '#1e3a8a')
}

/** A small chevron pointing along the line, drawn once and tinted by the layer (SDF). */
function arrowImage() {
  const size = 24
  const canvas = document.createElement('canvas')
  canvas.width = size
  canvas.height = size
  const context = canvas.getContext('2d')!
  context.strokeStyle = '#000'
  context.lineWidth = 3.5
  context.lineCap = 'round'
  context.lineJoin = 'round'
  context.beginPath()
  context.moveTo(8, 6)
  context.lineTo(16, 12)
  context.lineTo(8, 18)
  context.stroke()
  return context.getImageData(0, 0, size, size)
}

/** Stops joined in order, one line per leg, so each leg carries one arrow at its middle. */
function routeLegs(stops: RouteStop[]) {
  return {
    type: 'FeatureCollection' as const,
    features: stops.slice(1).map((stop, index) => ({
      type: 'Feature' as const,
      properties: { leg: index + 1 },
      geometry: {
        type: 'LineString' as const,
        coordinates: [
          [stops[index]!.longitude, stops[index]!.latitude],
          [stop.longitude, stop.latitude],
        ],
      },
    })),
  }
}

function addRoute(map: MapLibreMap, stops: RouteStop[], dark: boolean) {
  if (!map.hasImage(ARROW_IMAGE)) map.addImage(ARROW_IMAGE, arrowImage(), { sdf: true })
  const color = lineColor(dark)
  map.addSource(SOURCE, { type: 'geojson', data: routeLegs(stops) })
  map.addLayer({
    id: LINE_LAYER,
    type: 'line',
    source: SOURCE,
    layout: { 'line-cap': 'round' },
    paint: {
      'line-color': color,
      'line-width': 2,
      'line-opacity': 0.75,
      'line-dasharray': [1.5, 2],
    },
  })
  map.addLayer({
    id: ARROW_LAYER,
    type: 'symbol',
    source: SOURCE,
    layout: {
      'symbol-placement': 'line-center',
      'icon-image': ARROW_IMAGE,
      'icon-size': 0.75,
      'icon-rotation-alignment': 'map',
      'icon-allow-overlap': true,
      'icon-ignore-placement': true,
    },
    paint: { 'icon-color': color, 'icon-opacity': 0.9 },
  })
}

function fit(map: MapLibreMap, stops: RouteStop[], bounds: LngLatBounds) {
  if (stops.length === 1) {
    // One stop: the city around it.
    map.jumpTo({ center: [stops[0]!.longitude, stops[0]!.latitude], zoom: 10 })
    return
  }
  for (const stop of stops) bounds.extend([stop.longitude, stop.latitude])
  const narrow = (map.getContainer().clientWidth || 600) < 480
  map.fitBounds(bounds, { padding: narrow ? 36 : 56, maxZoom: 11, duration: 0 })
}

function stopElement(stop: RouteStop, onSelect: (stop: RouteStop) => void) {
  const button = document.createElement('button')
  button.type = 'button'
  button.dataset.stop = String(stop.number)
  button.title = stopTitle(stop)
  button.setAttribute(
    'aria-label',
    `Stop ${stop.number}: ${stopTitle(stop)}${stop.current ? ', latest' : ''}`,
  )
  button.setAttribute('aria-pressed', 'false')
  button.className = cn(
    'flex cursor-pointer items-center justify-center rounded-full border-2 font-semibold tabular-nums shadow-sm outline-none',
    'focus-visible:ring-[3px] focus-visible:ring-ring/60 aria-pressed:ring-[3px] aria-pressed:ring-ring',
    stop.current
      ? 'size-8 border-white bg-route-current text-sm text-white dark:border-neutral-900'
      : 'size-6 border-route-actual bg-background text-[11px] text-route-actual dark:border-moving dark:text-moving',
  )
  button.textContent = String(stop.number)
  button.addEventListener('click', (event) => {
    event.stopPropagation()
    onSelect(stop)
  })
  return button
}

export function ParcelRouteMap({
  stops,
  label,
  selected = null,
  onSelect,
}: {
  stops: RouteStop[]
  label: string
  /** The stop picked on the map or in the timeline, by number. */
  selected?: number | null
  onSelect?: (stop: RouteStop) => void
}) {
  const element = useRef<HTMLDivElement>(null)
  const buttons = useRef(new Map<number, HTMLButtonElement>())
  const select = useRef(onSelect)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    select.current = onSelect
  })

  useEffect(() => {
    const container = element.current
    if (!container || stops.length === 0) return
    let disposed = false
    let map: MapLibreMap | null = null
    let observer: MutationObserver | null = null
    const markers: Marker[] = []
    const placed = buttons.current

    void (async () => {
      const [maplibre] = await Promise.all([
        import('maplibre-gl'),
        import('maplibre-gl/dist/maplibre-gl.css'),
      ])
      if (disposed) return
      maplibre.setWorkerUrl(workerUrl)
      let dark = isDark()
      const latest = stops.at(-1)!
      try {
        map = new maplibre.Map({
          container,
          style: dark ? MAP_STYLES.dark : MAP_STYLES.light,
          attributionControl: { compact: true },
          cooperativeGestures: true,
          dragRotate: false,
          touchPitch: false,
          center: [latest.longitude, latest.latitude],
          zoom: 10,
        })
      } catch {
        setFailed(true)
        return
      }
      const route = map
      route.addControl(new maplibre.NavigationControl({ showCompass: false }), 'top-left')
      // The route sits on whichever basemap is loaded, light or dark.
      route.on('style.load', () => {
        if (!route.getSource(SOURCE)) addRoute(route, stops, dark)
      })
      // Framed once the map knows its own size (a panel that just opened may still be settling).
      route.once('load', () => {
        route.resize()
        fit(route, stops, new maplibre.LngLatBounds())
        // Attribution stays one tap away rather than covering a small map.
        container
          .querySelector('.maplibregl-ctrl-attrib.maplibregl-compact-show')
          ?.classList.remove('maplibregl-compact-show')
      })

      for (const stop of stops) {
        const button = stopElement(stop, (picked) => select.current?.(picked))
        button.setAttribute('aria-pressed', stop.number === selected ? 'true' : 'false')
        placed.set(stop.number, button)
        markers.push(
          new maplibre.Marker({ element: button, anchor: 'center' })
            .setLngLat([stop.longitude, stop.latitude])
            .addTo(route),
        )
      }

      // Follow Helm's light and dark themes.
      observer = new MutationObserver(() => {
        const next = isDark()
        if (next === dark) return
        dark = next
        route.setStyle(dark ? MAP_STYLES.dark : MAP_STYLES.light)
      })
      observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
    })().catch(() => {
      if (!disposed) setFailed(true)
    })

    return () => {
      disposed = true
      observer?.disconnect()
      for (const marker of markers) marker.remove()
      placed.clear()
      map?.remove()
    }
    // A new list of stops redraws the map; picking a stop only marks it (below).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stops])

  useEffect(() => {
    for (const [number, button] of buttons.current) {
      button.setAttribute('aria-pressed', number === selected ? 'true' : 'false')
    }
  }, [selected])

  if (failed) {
    // No WebGL on this device: the stops as text, still in order.
    return (
      <ol
        className="space-y-1 rounded-lg border border-dashed p-3 text-sm"
        aria-label={label}
        data-testid="parcel-route-list"
      >
        {stops.map((stop) => (
          <li key={stop.number} className={cn(stop.current && 'font-medium')}>
            {stop.number}. {stopTitle(stop)}
          </li>
        ))}
      </ol>
    )
  }

  return (
    <div
      ref={element}
      className="h-56 w-full overflow-hidden rounded-lg border sm:h-72"
      role="group"
      aria-label={label}
      data-testid="parcel-route-map"
    />
  )
}
